import { Card, Button } from "flowbite-react";
import { HiOutlineLightBulb, HiRefresh } from "react-icons/hi";
import { useTranslation, useToast } from "@repo/core";
import { useState, useRef, useEffect } from "react";
import { useChatWithReasoning, type ChatMessage } from "@repo/api";
import { useWebSocket } from "../../../hooks/useWebSocket";
import { useReasoningSource, type ReasoningVariant } from "./useReasoningSource";

/**
 * Formats markdown text to React elements
 * Supports: **bold**, newlines
 */
const formatMarkdown = (text: string): React.ReactNode[] => {
    // Split by newlines first
    const lines = text.split('\n');

    return lines.map((line, lineIndex) => {
        // Process bold text (**text**)
        const parts: React.ReactNode[] = [];
        let lastIndex = 0;
        const boldRegex = /\*\*(.*?)\*\*/g;
        let match;

        while ((match = boldRegex.exec(line)) !== null) {
            // Add text before the match
            if (match.index > lastIndex) {
                parts.push(line.slice(lastIndex, match.index));
            }
            // Add bold text
            parts.push(<strong key={`bold-${lineIndex}-${match.index}`}>{match[1]}</strong>);
            lastIndex = match.index + match[0].length;
        }

        // Add remaining text
        if (lastIndex < line.length) {
            parts.push(line.slice(lastIndex));
        }

        // If no bold was found, just use the line
        if (parts.length === 0) {
            parts.push(line);
        }

        // Wrap in span with line break (except for last line)
        return (
            <span key={`line-${lineIndex}`}>
                {parts}
                {lineIndex < lines.length - 1 && <br />}
            </span>
        );
    });
};

interface Message {
    id: string;
    text: string;
    sender: "user" | "bot";
    timestamp: Date;
}

interface ReasoningChatProps {
    variant: ReasoningVariant;
    userId?: string;
    strategyId?: string;
    modelId?: string;
    period?: string;
    rebalanceDate?: string;
    isLoading?: boolean;
    maxHeight?: number;
}

export const ReasoningChat = ({ variant, userId, strategyId, modelId, period, rebalanceDate, isLoading = false, maxHeight }: ReasoningChatProps) => {
    const { tStrategy } = useTranslation();
    const { addErrorMessage, addSuccessMessage } = useToast();
    const { pendingReasoningRequests, addPendingReasoningRequest, removePendingReasoningRequest } = useWebSocket();
    const [messages, setMessages] = useState<Message[]>([]);
    const [inputValue, setInputValue] = useState("");
    const [isTyping, setIsTyping] = useState(false);
    const {
        reasoning,
        isLoading: isReasoningLoading,
        error,
        generate,
        pendingKey,
        isGeneratePending,
        chatTarget,
        language,
    } = useReasoningSource({ variant, userId, strategyId, modelId, period, rebalanceDate });
    const chatMutation = useChatWithReasoning();
    const messagesEndRef = useRef<HTMLDivElement>(null);
    const scrollContainerRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);
    const reasoningLoadedRef = useRef(false);

    const scrollToBottom = () => {
        if (scrollContainerRef.current) {
            scrollContainerRef.current.scrollTop = scrollContainerRef.current.scrollHeight;
        }
    };

    useEffect(() => {
        scrollToBottom();
    }, [messages, isTyping]);

    // Generation state lives in WebSocketContext (not local state) so it survives remounts and
    // is shared by the mobile/desktop instances on the strategy detail page. WebSocketMessageHandler
    // clears the key and refetches the reasoning query when the "dataPrepared" broadcast arrives.
    const isGenerating = !!pendingKey && pendingReasoningRequests.has(pendingKey);

    // Reset state when rebalanceDate changes to allow fresh data load
    useEffect(() => {
        reasoningLoadedRef.current = false;
        setMessages([]);
    }, [rebalanceDate, strategyId, modelId, period, variant]);

    // Add reasoning data as chat messages when fetched (only once per date)
    useEffect(() => {
        if (!reasoning?.item || reasoningLoadedRef.current) return;
        reasoningLoadedRef.current = true;

        // Clean reasoning text by removing markers like [DATA], [CONTEXT], etc.
        const cleanReasoningText = (text: string): string => {
            return text
                .replace(/\[DATA\]/gi, '')
                .replace(/\[CONTEXT\]/gi, '')
                .replace(/\s{2,}/g, ' ') // Collapse multiple spaces
                .trim();
        };

        const reasoningProperties = [
            { key: "crossFactorSignals", label: tStrategy("reasoning.crossFactorSignals") },
            { key: "dataQualityAlerts", label: tStrategy("reasoning.dataQualityAlerts") },
            { key: "factorConfidence", label: tStrategy("reasoning.factorConfidence") },
            { key: "factorDrivers", label: tStrategy("reasoning.factorDrivers") },
            { key: "marketContext", label: tStrategy("reasoning.marketContext") },
            { key: "nearBoundary", label: tStrategy("reasoning.nearBoundary") },
            { key: "notableShifts", label: tStrategy("reasoning.notableShifts") },
            { key: "rebalanceRecommendation", label: tStrategy("reasoning.rebalanceRecommendation") },
        ] as const;

        const newMessages: Message[] = reasoningProperties
            .filter(({ key }) => reasoning.item[key])
            .map(({ key, label }) => ({
                id: `reasoning-${key}`,
                text: `**${label}**\n${cleanReasoningText(reasoning.item[key])}`,
                sender: "bot" as const,
                timestamp: new Date(),
            }));

        if (newMessages.length > 0) {
            setMessages((prev) => [...prev, ...newMessages]);
        }
    }, [reasoning, tStrategy]);

    // Don't render until reasoning data is successfully loaded
    // This check must come AFTER all hooks to comply with Rules of Hooks

    // Handle case when reasoning data is not available (404 error)
    const isReasoningNotFound = error?.status === 404;

    if (isReasoningLoading) {
        return null; // Still loading
    }

    // If reasoning data doesn't exist and not generating, show generate button
    // If generating, show progress state
    if (isReasoningNotFound || !reasoning?.item) {
        const handleGenerateReport = async () => {
            if (!pendingKey) return;

            try {
                // Track this request before the call, so the "dataPrepared" broadcast is recognized
                // as ours and every instance shows the generating state right away.
                addPendingReasoningRequest(pendingKey);
                await generate();

                // Lambda invoked asynchronously - show success message
                // WebSocket will notify when reasoning is complete (type: "dataPrepared")
                addSuccessMessage(tStrategy("reasoning.generateSuccess"));
            } catch (error) {
                console.error("Failed to generate reasoning:", error);
                addErrorMessage(tStrategy("reasoning.generateError"));
                removePendingReasoningRequest(pendingKey);
            }
        };

        return (
            <Card
                className="w-full flex flex-col overflow-hidden"
                style={{ height: maxHeight ? `${maxHeight}px` : 'auto' }}
            >
                <div className="flex items-center gap-2 mb-4 flex-shrink-0">
                    <HiOutlineLightBulb className="w-5 h-5 text-blue-600" />
                    <h3 className="text-lg font-semibold text-gray-900">
                        {tStrategy("reasoning.title")}
                    </h3>
                </div>

                <div className="flex-1 flex flex-col items-center justify-center p-8 text-center">
                    {isGenerating ? (
                        // Show generating state
                        <>
                            <div className="mb-6">
                                <div className="w-16 h-16 bg-blue-50 rounded-full flex items-center justify-center mx-auto mb-4 relative">
                                    <HiRefresh className="w-8 h-8 text-blue-600 animate-spin" />
                                </div>
                                <h4 className="text-lg font-semibold text-gray-900 mb-2">
                                    {tStrategy("reasoning.generatingTitle")}
                                </h4>
                                <p className="text-sm text-gray-600 max-w-md">
                                    {tStrategy("reasoning.generatingDescription")}
                                </p>
                            </div>
                            <div className="flex items-center gap-2 text-blue-600">
                                <div className="w-2 h-2 bg-blue-600 rounded-full animate-bounce"></div>
                                <div className="w-2 h-2 bg-blue-600 rounded-full animate-bounce" style={{ animationDelay: "0.1s" }}></div>
                                <div className="w-2 h-2 bg-blue-600 rounded-full animate-bounce" style={{ animationDelay: "0.2s" }}></div>
                            </div>
                        </>
                    ) : (
                        // Show generate button
                        <>
                            <div className="mb-6">
                                <div className="w-16 h-16 bg-blue-50 rounded-full flex items-center justify-center mx-auto mb-4">
                                    <HiOutlineLightBulb className="w-8 h-8 text-blue-600" />
                                </div>
                                <h4 className="text-lg font-semibold text-gray-900 mb-2">
                                    {tStrategy("reasoning.notAvailableTitle")}
                                </h4>
                                <p className="text-sm text-gray-600 max-w-md">
                                    {tStrategy("reasoning.notAvailableDescription")}
                                </p>
                            </div>

                            <Button
                                color="blue"
                                onClick={handleGenerateReport}
                                disabled={isGeneratePending}
                                className="min-w-[200px]"
                            >
                                <HiRefresh className="w-4 h-4 mr-2" />
                                {tStrategy("reasoning.generateButton")}
                            </Button>
                        </>
                    )}
                </div>
            </Card>
        );
    }

    if (error) {
        return null; // Other errors - don't show anything
    }

    const handleSendMessage = async () => {
        if (!inputValue.trim() || !userId || !modelId || !rebalanceDate) return;

        // Blur the input to prevent page scroll
        inputRef.current?.blur();

        // Add user message
        const userMessage: Message = {
            id: `user-${Date.now()}`,
            text: inputValue,
            sender: "user",
            timestamp: new Date(),
        };

        setMessages((prev) => [...prev, userMessage]);
        setInputValue("");
        setIsTyping(true);

        // Build conversation history from existing messages
        const conversationHistory: ChatMessage[] = messages
            .filter(msg => !msg.id.startsWith('reasoning-')) // Exclude initial reasoning messages
            .map(msg => ({
                role: msg.sender === "user" ? "user" : "assistant",
                content: msg.text
            }));

        try {
            // Call chat API
            const response = await chatMutation.mutateAsync({
                ...chatTarget,
                modelId,
                rebalanceDate,
                question: inputValue,
                conversationHistory,
                language,
            });

            // Add bot response
            const botMessage: Message = {
                id: `bot-${Date.now()}`,
                text: response.answer,
                sender: "bot",
                timestamp: new Date(),
            };
            setMessages((prev) => [...prev, botMessage]);
        } catch (error) {
            console.error("Chat API error:", error);
            addErrorMessage(tStrategy("reasoning.error"), "error");

            // Add error message to chat
            const errorMessage: Message = {
                id: `bot-error-${Date.now()}`,
                text: tStrategy("reasoning.errorMessage"),
                sender: "bot",
                timestamp: new Date(),
            };
            setMessages((prev) => [...prev, errorMessage]);
        } finally {
            setIsTyping(false);
        }
    };

    const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === "Enter") {
            e.preventDefault();
            handleSendMessage();
        }
    };

    return (
        <Card
            className="w-full flex flex-col overflow-hidden"
            style={{ height: maxHeight ? `${maxHeight}px` : 'auto' }}
        >
            <div className="flex items-center gap-2 mb-4 flex-shrink-0">
                <HiOutlineLightBulb className="w-5 h-5 text-blue-600" />
                <h3 className="text-lg font-semibold text-gray-900">
                    {tStrategy("reasoning.title")}
                </h3>
            </div>

            {isLoading ? (
                <div className="animate-pulse space-y-4 flex-1">
                    <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                    <div className="h-4 bg-gray-200 rounded w-full"></div>
                    <div className="h-4 bg-gray-200 rounded w-5/6"></div>
                    <div className="h-4 bg-gray-200 rounded w-full"></div>
                    <div className="h-4 bg-gray-200 rounded w-2/3"></div>
                </div>
            ) : (
                <div className="flex-1 overflow-y-auto -mx-6 px-6 relative min-h-0" ref={scrollContainerRef}>
                    {/* Messages area */}
                    <div className="pb-24 space-y-4">
                        {messages.length === 0 ? (
                            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                                <p className="text-sm text-gray-700">
                                    {tStrategy("reasoning.placeholder")}
                                </p>
                            </div>
                        ) : (
                            messages.map((message) => (
                                <div
                                    key={message.id}
                                    className={`flex ${message.sender === "user" ? "justify-end" : "justify-start"}`}
                                >
                                    <div
                                        className={`max-w-[80%] rounded-lg px-4 py-2 ${message.sender === "user"
                                            ? "bg-blue-600 text-white"
                                            : "bg-gray-100 text-gray-900"
                                            }`}
                                    >
                                        <p className="text-sm">{formatMarkdown(message.text)}</p>
                                    </div>
                                </div>
                            ))
                        )}
                        {isTyping && (
                            <div className="flex justify-start">
                                <div className="bg-gray-100 rounded-lg px-4 py-2">
                                    <div className="flex gap-1">
                                        <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"></div>
                                        <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: "0.1s" }}></div>
                                        <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: "0.2s" }}></div>
                                    </div>
                                </div>
                            </div>
                        )}
                        <div ref={messagesEndRef} />
                    </div>

                    {/* Sticky input - sticks to bottom of scrollable area */}
                    <div className="sticky bottom-0 bg-white pt-4 pb-6 border-t border-gray-200 -mx-6 px-6">
                        <div className="flex items-center gap-2">
                            <input
                                ref={inputRef}
                                type="text"
                                value={inputValue}
                                onChange={(e) => setInputValue(e.target.value)}
                                onKeyDown={handleKeyPress}
                                placeholder={tStrategy("reasoning.inputPlaceholder")}
                                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            />
                            <button
                                type="button"
                                onClick={handleSendMessage}
                                disabled={!inputValue.trim()}
                                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors whitespace-nowrap disabled:bg-gray-300 disabled:cursor-not-allowed"
                            >
                                {tStrategy("reasoning.send")}
                            </button>
                        </div>
                    </div>
                    {isReasoningLoading && (
                        <div className="flex justify-center items-center py-4">
                            <p className="text-sm text-gray-500">{tStrategy("reasoning.loading")}</p>
                        </div>
                    )}
                </div>
            )}
        </Card>
    );
};
